/**
 * Roadmap — the Memory Lane care journey (accurate interim version).
 *
 * Four clearly separated ideas:
 *   1. JOURNEY AREAS — what the family is dealing with (7 areas, compact
 *      road; details open in a drawer/bottom sheet, not giant inline cards).
 *   2. SERVICES & PROVIDERS — who is actually helping ("Who is on the road
 *      with you" map/list, statuses referred/active/paused/ended, episodes
 *      preserved).
 *   3. SUPPORT LANE — Lane, wellness, care coordination, community support:
 *      a parallel lane beside the road, available at every point.
 *   4. MEMORY LANE FOLDER — records and history that travel with the family.
 * Plus TRANSITION PINS — dated change events that can happen anywhere.
 *
 * FAST labels remain Alzheimer's-only and never drive the structure.
 */

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { api, getSessionId } from "@/lib/api";
import {
    JOURNEY_AREAS, SERVICES,
    currentStageKey, shouldShowFast,
} from "@/lib/roadmapStages";
import { track, EVENTS } from "@/lib/analytics";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";
import CareJourneyFlow from "@/components/CareJourneyFlow";
import { JourneyViewToggle } from "@/components/JourneyViewToggle";
import JourneyWindingPath from "@/components/JourneyWindingPath";
import { CareDetailsDialog } from "@/components/CareDetailsDialog";
import {
    MapPin, Sparkles, MessageCircle, ChevronRight,
    Eye, Search, Flag, Sun, Shield, Moon, Sunrise, Stethoscope,
    FileText, Users, Info, ArrowRight, PlusCircle, CheckCircle2,
    Zap, LifeBuoy, HeartHandshake, FolderOpen, Pencil, History, Phone,
} from "lucide-react";

const ICON_MAP = {
    eye: Eye, search: Search, flag: Flag, sun: Sun,
    shield: Shield, moon: Moon, sunrise: Sunrise,
};

// ---- Statuses for ACTUAL services (never applied to suggestions) ----
const STATUS_OPTIONS = [
    { value: "referred", label: "Referred" },
    { value: "active", label: "Active" },
    { value: "paused", label: "Paused" },
    { value: "ended", label: "Ended" },
];
const STATUS_STYLE = {
    referred: "bg-[#DFE8FC] text-[#2D62D9]",
    active: "bg-[#D7F3E5] text-[#177A4B]",
    paused: "bg-[#FDF3D7] text-[#8A6D1A]",
    ended: "bg-cream-dark/60 text-[#4C5551]",
};
const normalizeStatus = (s) => (s === "starting_soon" ? "referred" : s);

const fmtDate = (d) => {
    if (!d) return "";
    const dt = new Date(`${String(d).slice(0, 10)}T12:00:00`);
    return Number.isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// ---- "Something changed" event options (saved as dated transitions) ----
const CHANGE_OPTIONS = [
    { key: "symptom", label: "New symptom or behavior", prompt: "Something changed: we're seeing a new symptom or behavior. Can you help me think through what to do?" },
    { key: "hospital", label: "Hospital or emergency visit", prompt: "Something changed: there was a hospital or emergency visit. What should I do next and what should I track?" },
    { key: "medication", label: "Medication change", prompt: "Something changed: a medication was changed. What should I watch for and ask about?" },
    { key: "diagnosis", label: "New diagnosis", prompt: "Something changed: we received a new diagnosis. Help me understand what this means for our care plan." },
    { key: "provider", label: "New provider or program", prompt: "Something changed: a new provider or program is starting. How do I get them up to speed without starting over?" },
    { key: "service_started", label: "Service started", prompt: "Something changed: a new service just started. What should I set up in the first weeks?" },
    { key: "service_paused", label: "Service paused", prompt: "Something changed: a service we relied on is paused. Help me bridge the gap." },
    { key: "service_ended", label: "Service ended", prompt: "Something changed: a service we relied on ended. Help me figure out what comes next." },
    { key: "move", label: "Move or living-arrangement change", prompt: "Something changed: there's a move or living-arrangement change. Help me plan the transition." },
    { key: "caregiver", label: "Caregiver change", prompt: "Something changed: the caregiving situation itself changed. Help me rebalance the plan." },
    { key: "safety", label: "Safety concern", prompt: "Something changed: I have a safety concern. Can you help me assess it and act on it?" },
    { key: "other", label: "Other", prompt: "Something changed in our situation. Can I talk it through with you?" },
];

const SERVICE_CHANGE_KEYS = new Set(["service_started", "service_paused", "service_ended", "provider"]);

function useIsMobile() {
    const [mobile, setMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
    useEffect(() => {
        const mq = window.matchMedia("(max-width: 767px)");
        const fn = (e) => setMobile(e.matches);
        mq.addEventListener("change", fn);
        return () => mq.removeEventListener("change", fn);
    }, []);
    return mobile;
}

export default function Roadmap() {
    const sid = getSessionId();
    const isMobile = useIsMobile();
    const [profile, setProfile] = useState(null);
    const [intake, setIntake] = useState(null);
    const [providers, setProviders] = useState([]);
    const [events, setEvents] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [changedOpen, setChangedOpen] = useState(false);

    // Dashboard's "Something changed" button deep-links here via #changed.
    useEffect(() => {
        if (window.location.hash === "#changed") {
            setChangedOpen(true);
            window.history.replaceState(null, "", window.location.pathname);
        }
    }, []);
    const [whereOpen, setWhereOpen] = useState(false);
    const [areaSheetKey, setAreaSheetKey] = useState(null);
    const [pinEvent, setPinEvent] = useState(null);
    const [supportOpen, setSupportOpen] = useState(false);
    const [whatsNewDismissed, setWhatsNewDismissed] = useState(
        () => typeof window !== "undefined" && localStorage.getItem("ml_roadmap_v4_intro") === "1"
    );
    const dismissWhatsNew = () => {
        setWhatsNewDismissed(true);
        localStorage.setItem("ml_roadmap_v4_intro", "1");
    };
    const [journeyView, setJourneyView] = useState(
        () => (typeof window !== "undefined" && localStorage.getItem("ml_journey_view")) || "path"
    );
    const changeJourneyView = (v) => {
        setJourneyView(v);
        localStorage.setItem("ml_journey_view", v);
    };
    const [careDetailsOpen, setCareDetailsOpen] = useState(false);

    useEffect(() => {
        let active = true;
        Promise.all([
            api.get(`/intake/${sid}`).catch(() => ({ data: null })),
            api.get(`/profile/${sid}`).catch(() => ({ data: {} })),
            api.get(`/care-team/providers/${sid}`).catch(() => ({ data: { providers: [] } })),
            api.get(`/roadmap/transitions/${sid}`).catch(() => ({ data: { events: [] } })),
        ]).then(([ir, pr, cr, tr]) => {
            if (!active) return;
            setIntake(ir.data || null);
            setProfile(pr.data || null);
            setProviders(cr.data?.providers || []);
            setEvents(tr.data?.events || []);
        }).finally(() => { if (active) setLoaded(true); });
        return () => { active = false; };
    }, [sid]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        if (sessionStorage.getItem("ml_ga_roadmap_journey_viewed")) return;
        sessionStorage.setItem("ml_ga_roadmap_journey_viewed", "1");
        track(EVENTS.ROADMAP_VIEWED, { session_id: sid, variant: "journey_v4_interim" });
    }, [sid]);

    const merged = useMemo(() => ({
        predx: intake?.predx,
        estimated_stage: intake?.estimated_stage,
        has_formal_dx: profile?.has_formal_dx,
        bereaved: profile?.bereaved,
        dementia_type: profile?.dementia_type || profile?.condition || profile?.diagnosis || "",
    }), [intake, profile]);

    const computedKey = useMemo(() => (loaded ? currentStageKey(merged) : null), [loaded, merged]);
    const primaryKey = profile?.primary_journey_area || computedKey;
    const alsoKeys = useMemo(
        () => (profile?.also_dealing_with || []).filter(k => k !== primaryKey && JOURNEY_AREAS.some(a => a.key === k)),
        [profile, primaryKey],
    );
    const alzheimersMode = useMemo(() => shouldShowFast(merged), [merged]);
    const visitedKeys = useMemo(
        () => (profile?.visited_areas || []).filter(k => JOURNEY_AREAS.some(a => a.key === k)),
        [profile],
    );

    const inCareTeamKeys = useMemo(() => {
        const set = new Set();
        for (const p of providers) {
            if (normalizeStatus(p.status) !== "ended") set.add(p.service_key);
        }
        return set;
    }, [providers]);

    const activeCount = providers.filter(p => normalizeStatus(p.status) === "active").length;
    const latestEvent = events[0] || null;
    const primaryArea = JOURNEY_AREAS.find(a => a.key === primaryKey) || null;

    const eventsByArea = useMemo(() => {
        const map = {};
        const unplaced = [];
        for (const ev of events) {
            if (ev.journey_area) (map[ev.journey_area] = map[ev.journey_area] || []).push(ev);
            else unplaced.push(ev);
        }
        return { map, unplaced };
    }, [events]);

    const saveWhere = async (primary, also) => {
        try {
            const r = await api.put(`/profile/${sid}`, {
                primary_journey_area: primary || "",
                also_dealing_with: also,
            });
            setProfile(r.data || { ...(profile || {}), primary_journey_area: primary, also_dealing_with: also });
            setWhereOpen(false);
            toast.success("Your place on the journey is updated.");
        } catch {
            toast.error("Couldn't save. Please try again.");
        }
    };

    const updateProviderStatus = async (p, patch) => {
        try {
            const r = await api.put(`/care-team/providers/${sid}/${p.id}`, patch);
            setProviders(prev => prev.map(x => x.id === p.id ? r.data.provider : x));
            if (patch.status) toast.success(`${p.name} marked ${STATUS_OPTIONS.find(o => o.value === patch.status)?.label || patch.status}.`);
            else toast.success("Saved.");
        } catch {
            toast.error("Couldn't update. Please try again.");
        }
    };

    const onEventSaved = (ev) => setEvents(prev => [ev, ...prev]);

    const toggleVisited = async (key) => {
        const next = visitedKeys.includes(key) ? visitedKeys.filter(k => k !== key) : [...visitedKeys, key];
        try {
            const r = await api.put(`/profile/${sid}`, { visited_areas: next });
            setProfile(r.data || { ...(profile || {}), visited_areas: next });
            if (!visitedKeys.includes(key)) {
                track(EVENTS.ROADMAP_STEP_COMPLETED, { session_id: sid, stage: key });
                track(EVENTS.ROADMAP_COMPLETED, { session_id: sid, stage: key, source: "stage_visited" });
            }
            toast.success(visitedKeys.includes(key) ? "Unmarked." : "Marked as \"been here\" — the road remembers.");
        } catch {
            toast.error("Couldn't save. Please try again.");
        }
    };

    return (
        <div className="max-w-5xl mx-auto space-y-5 animate-fade-in-up" data-testid="roadmap-page">
            <JourneyViewToggle active="roadmap" first={(profile?.loved_one_name || "").split(" ")[0]} />
            {/* -------------------- Header -------------------- */}
            <header
                data-testid="roadmap-header"
                className="relative overflow-hidden rounded-3xl border border-cream-dark bg-white p-6 md:p-8 shadow-sm"
            >
                <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-br from-teal-soft/70 via-transparent to-transparent pointer-events-none" />
                <div aria-hidden="true" className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-teal-soft/60 blur-2xl pointer-events-none" />
                <svg aria-hidden="true" className="absolute right-2 bottom-2 hidden md:block pointer-events-none" width="230" height="96" viewBox="0 0 230 96" fill="none">
                    <path d="M0 88 C 70 88, 90 26, 160 26 L 230 26" stroke="#1D8A85" strokeWidth="3" strokeDasharray="9 9" strokeLinecap="round" opacity="0.45" />
                    <circle cx="160" cy="26" r="7" fill="#1D8A85" opacity="0.9" />
                    <circle cx="160" cy="26" r="12" fill="#1D8A85" opacity="0.18" />
                </svg>
                <div className="relative">
                    <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark flex items-center gap-1.5 mb-2">
                        <MapPin size={13} strokeWidth={2.2} /> The whole journey, one place
                    </p>
                    <h1 className="text-4xl md:text-5xl font-heading font-semibold tracking-tight text-[#12776F]" data-testid="roadmap-title">
                        Your Memory Lane Roadmap
                    </h1>
                    <p className="mt-3 text-base md:text-lg text-[#4C5551] leading-relaxed max-w-2xl" data-testid="roadmap-subtitle">
                        There is no single path through dementia care. You may move forward, revisit earlier needs,
                        skip parts of the journey, or deal with several needs at the same time. Providers and programs
                        may come and go, but Memory Lane keeps your story, care team, documents, questions, and next
                        steps together.
                    </p>
                    <p className="mt-2 text-xs text-[#6E7A74] italic" data-testid="roadmap-not-clinical-note">
                        This roadmap is personalized guidance, not a clinical staging tool.
                    </p>
                    <p className="mt-2 text-xs text-[#4C5551] max-w-2xl" data-testid="roadmap-changes-anytime-note">
                        <Zap size={11} strokeWidth={2.2} className="inline -mt-0.5 mr-1 text-teal-dark" />
                        Hospital visits, provider changes, new symptoms, service changes, moves, caregiver changes,
                        and sudden changes in function can happen during any part of the journey.
                    </p>
                </div>
            </header>

            {/* One-time orientation for returning users — the roadmap changed
                around them; give them a 20-second map of what's new. */}
            {loaded && !whatsNewDismissed && (intake || providers.length > 0 || events.length > 0) && (
                <section
                    data-testid="roadmap-whats-new"
                    className="rounded-3xl border border-[#D5E6E4] p-4 md:p-5"
                    style={{ background: "linear-gradient(120deg, #F3F8F5 0%, #FDFBF4 100%)" }}
                >
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <p className="text-sm font-semibold text-[#2D3748] flex items-center gap-1.5">
                                <Sparkles size={14} strokeWidth={2} className="text-teal-dark" /> Your roadmap got a gentle upgrade
                            </p>
                            <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-[#4C5551]">
                                <li>• <span className="font-medium text-[#2D3748]">Who is on the road with you</span> — your real care team as a living map</li>
                                <li>• <span className="font-medium text-[#2D3748]">Change pins</span> — "Something changed" now saves dated events on the road</li>
                                <li>• <span className="font-medium text-[#2D3748]">Support lane</span> — Lane, wellness, coordination and community, always beside you</li>
                                <li>• <span className="font-medium text-[#2D3748]">Memory Lane Folder</span> — your records and history travel with the family</li>
                            </ul>
                            <p className="mt-2 text-xs text-[#6E7A74]">Everything you already entered is still here — nothing was lost.</p>
                        </div>
                        <button
                            type="button"
                            onClick={dismissWhatsNew}
                            data-testid="roadmap-whats-new-dismiss"
                            className="ml-btn-secondary !min-h-[36px] !py-1.5 !px-3 text-xs shrink-0"
                        >
                            Got it
                        </button>
                    </div>
                </section>
            )}

            {/* -------------------- Your road right now -------------------- */}            <section className="ml-card p-4 md:p-5" data-testid="roadmap-road-now" style={{ borderLeft: "5px solid #1D8A85" }}>
                <p className="text-[10px] uppercase tracking-wider font-semibold text-teal-dark">Your road right now</p>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                    <p data-testid="road-now-primary">
                        <span className="text-[#6E7A74]">Primary area:</span>{" "}
                        {primaryArea ? (
                            <span className="font-semibold" style={{ color: primaryArea.color }}>{primaryArea.title}</span>
                        ) : <span className="italic text-[#6E7A74]">not set yet</span>}
                    </p>
                    <p data-testid="road-now-active-count">
                        <span className="text-[#6E7A74]">Active services:</span>{" "}
                        <span className="font-semibold text-[#1F2624]">{activeCount}</span>
                    </p>
                    <p data-testid="road-now-also">
                        <span className="text-[#6E7A74]">Also dealing with:</span>{" "}
                        {alsoKeys.length
                            ? alsoKeys.map(k => JOURNEY_AREAS.find(a => a.key === k)?.title).filter(Boolean).join(", ")
                            : <span className="italic text-[#6E7A74]">none added</span>}
                    </p>
                    <p data-testid="road-now-recent-change">
                        <span className="text-[#6E7A74]">Most recent change:</span>{" "}
                        {latestEvent
                            ? <span className="text-[#1F2624]">{latestEvent.label} · {fmtDate(latestEvent.date)}</span>
                            : <span className="italic text-[#6E7A74]">none recorded</span>}
                    </p>
                    {primaryArea?.next_steps?.[0] && (
                        <p className="sm:col-span-2" data-testid="road-now-next-action">
                            <span className="text-[#6E7A74]">Next important action:</span>{" "}
                            <span className="text-[#1F2624]">{primaryArea.next_steps[0]}</span>
                        </p>
                    )}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                    <button type="button" onClick={() => setChangedOpen(true)} className="ml-btn-primary text-sm !min-h-[42px] !py-2" data-testid="roadmap-something-changed-btn">
                        <Zap size={14} strokeWidth={2} /> Something changed
                    </button>
                    <button type="button" onClick={() => setWhereOpen(true)} className="ml-btn-secondary text-sm !min-h-[42px] !py-2" data-testid="roadmap-update-area-btn">
                        <MapPin size={14} strokeWidth={2} /> Update where we are
                    </button>
                    <Link to="/care-team" className="ml-btn-secondary text-sm !min-h-[42px] !py-2 !bg-white border border-teal/40 !text-teal-dark hover:!bg-teal-soft" data-testid="roadmap-add-service-btn">
                        <PlusCircle size={14} strokeWidth={2} /> Add a service
                    </Link>
                    <Link to="/care-history" className="ml-btn-secondary text-sm !min-h-[42px] !py-2 !bg-white border border-teal/40 !text-teal-dark hover:!bg-teal-soft" data-testid="roadmap-care-history-btn">
                        <FileText size={14} strokeWidth={2} /> Care history
                    </Link>
                    <button type="button" onClick={() => setCareDetailsOpen(true)}
                        className="ml-btn-secondary text-sm !min-h-[42px] !py-2 !bg-white border border-teal/40 !text-teal-dark hover:!bg-teal-soft"
                        data-testid="roadmap-update-care-details">
                        <Pencil size={14} strokeWidth={2} /> Update care details
                    </button>
                </div>
            </section>

            <CareDetailsDialog
                open={careDetailsOpen}
                onOpenChange={setCareDetailsOpen}
                sid={sid}
                profile={profile}
                onSaved={(patch) => setProfile(p => ({ ...(p || {}), ...patch }))}
            />

            {/* -------------------- Main grid: road + support lane ---------- */}
            <div className="md:grid md:grid-cols-[minmax(0,1fr)_200px] md:gap-5 md:items-start">
                <div className="space-y-5 min-w-0">
                    {/* ---------- Who is on the road with you ---------- */}
                    {loaded && (
                        <WhoIsOnTheRoad providers={providers} onUpdate={updateProviderStatus} />
                    )}

                    {/* ---------- The seven journey areas ---------- */}
                    <section aria-label="Memory Lane journey areas" data-testid="roadmap-journey" className="ml-card p-4 md:p-5">
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                            <div className="flex items-center gap-3">
                                <h2 className="text-[10px] uppercase tracking-wider font-semibold text-[#4C5551]">Your journey</h2>
                                <div className="flex rounded-full border border-cream-dark overflow-hidden text-[11px] font-semibold" data-testid="roadmap-journey-view-toggle">
                                    {["path", "list"].map(v => (
                                        <button key={v} type="button" onClick={() => changeJourneyView(v)}
                                            data-testid={`roadmap-journey-view-${v}`}
                                            className={`px-4 py-1 min-h-[44px] capitalize transition-colors ${journeyView === v ? "bg-teal text-white" : "bg-white text-[#4C5551] hover:bg-cream-light"}`}>
                                            {v}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {primaryKey && alsoKeys.length > 0 && (
                                <p className="text-xs text-[#4C5551]" data-testid="roadmap-also-dealing-summary">
                                    Also dealing with: {alsoKeys.map(k => JOURNEY_AREAS.find(a => a.key === k)?.title).filter(Boolean).join(", ")}
                                </p>
                            )}
                        </div>
                        <p className="mt-1 text-xs text-[#6E7A74] leading-snug" data-testid="roadmap-services-disclaimer">
                            A caregiver may be dealing with more than one journey area at once. The services listed
                            inside each area are examples, not a required sequence — a service may begin, pause, end,
                            or return during different parts of the journey.
                        </p>
                        {alzheimersMode && (
                            <p className="mt-1 text-xs text-[#6E7A74] italic" data-testid="roadmap-fast-disclaimer">
                                FAST clinical labels appear because Alzheimer&apos;s is selected or suspected in the patient profile.
                            </p>
                        )}

                        {eventsByArea.unplaced.length > 0 && (
                            <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="roadmap-unplaced-pins">
                                <span className="text-[10px] uppercase tracking-wider font-semibold text-[#6E7A74]">Recent changes:</span>
                                {eventsByArea.unplaced.slice(0, 6).map(ev => (
                                    <TransitionPin key={ev.id} event={ev} onOpen={() => setPinEvent(ev)} />
                                ))}
                            </div>
                        )}

                        {journeyView === "path" ? (
                            <JourneyWindingPath
                                areas={JOURNEY_AREAS}
                                primaryKey={primaryKey}
                                alsoKeys={alsoKeys}
                                visitedKeys={visitedKeys}
                                pinsByArea={eventsByArea.map}
                                onOpen={(k) => setAreaSheetKey(k)}
                                shareable
                            />
                        ) : (
                            <ol className="mt-4">
                                {JOURNEY_AREAS.map((area, idx) => (
                                    <JourneyAreaRow
                                        key={area.key}
                                        area={area}
                                        isFirst={idx === 0}
                                        isLast={idx === JOURNEY_AREAS.length - 1}
                                        isPrimary={area.key === primaryKey}
                                        isAlso={alsoKeys.includes(area.key)}
                                        isVisited={visitedKeys.includes(area.key)}
                                        pins={eventsByArea.map[area.key] || []}
                                        alzheimersMode={alzheimersMode}
                                        onOpen={() => setAreaSheetKey(area.key)}
                                        onOpenPin={setPinEvent}
                                    />
                                ))}
                            </ol>
                        )}
                    </section>

                    {!intake && !profile?.primary_journey_area && (
                        <section className="ml-card p-4 md:p-5 bg-teal-soft !border-teal-soft" data-testid="roadmap-intake-nudge">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <p className="text-sm text-[#1F2624] max-w-lg">
                                    <span className="font-medium">Want this personalized?</span> Three quiet minutes of intake — or
                                    use &ldquo;Update where we are&rdquo; to place your marker yourself.
                                </p>
                                <Link to="/intake" className="ml-btn-primary text-sm" data-testid="roadmap-goto-intake">
                                    Personalize <ArrowRight size={14} strokeWidth={1.8} />
                                </Link>
                            </div>
                        </section>
                    )}
                </div>

                {/* ---------- Support lane + Memory Lane Folder (desktop) ---------- */}
                <aside className="hidden md:block md:sticky md:top-20 space-y-3" data-testid="roadmap-support-lane">
                    <SupportLane />
                    <MemoryLaneFolder />
                </aside>
            </div>

            {/* ---------- Mobile: sticky Support stops button ----------
                Portaled to <body>: an ancestor's fade-in-up transform would
                otherwise become the containing block and break position:fixed. */}
            {createPortal(
                <button
                    type="button"
                    onClick={() => setSupportOpen(true)}
                    data-testid="roadmap-support-stops-btn"
                    className="md:hidden fixed bottom-[84px] right-4 z-40 inline-flex items-center gap-2 rounded-2xl bg-teal-dark text-white text-sm font-semibold px-4 py-3 min-h-[44px] shadow-lg"
                >
                    <LifeBuoy size={16} strokeWidth={2} /> Support stops
                </button>,
                document.body
            )}
            <Sheet open={supportOpen} onOpenChange={setSupportOpen}>
                <SheetContent side="bottom" className="rounded-t-3xl max-h-[80vh] overflow-y-auto" data-testid="support-stops-sheet">
                    <SheetHeader className="text-left">
                        <SheetTitle>Support available throughout the journey</SheetTitle>
                        <SheetDescription>
                            Pull over anytime. These supports stay available even when providers and programs change.
                        </SheetDescription>
                    </SheetHeader>
                    <div className="mt-3 space-y-2 pb-4">
                        {SUPPORT_ITEMS.map(({ key, label, to, icon: Icon, detail }) => (
                            <Link key={key} to={to} data-testid={`support-sheet-${key}`}
                                className="flex items-center gap-3 rounded-2xl border border-cream-dark bg-white px-4 py-3">
                                <span className="w-9 h-9 rounded-xl bg-teal-soft flex items-center justify-center shrink-0">
                                    <Icon size={16} strokeWidth={2} className="text-teal-dark" />
                                </span>
                                <span className="min-w-0">
                                    <span className="block text-sm font-medium text-[#1F2624]">{label}</span>
                                    <span className="block text-xs text-[#6E7A74]">{detail}</span>
                                </span>
                            </Link>
                        ))}
                        <Link to="/documents" data-testid="support-sheet-folder"
                            className="flex items-center gap-3 rounded-2xl border border-cream-dark bg-cream-light px-4 py-3">
                            <span className="w-9 h-9 rounded-xl bg-white flex items-center justify-center shrink-0 border border-cream-dark">
                                <FolderOpen size={16} strokeWidth={2} className="text-teal-dark" />
                            </span>
                            <span className="min-w-0">
                                <span className="block text-sm font-medium text-[#1F2624]">Your Memory Lane Folder</span>
                                <span className="block text-xs text-[#6E7A74]">Records, notes, and history that travel with you.</span>
                            </span>
                        </Link>
                    </div>
                </SheetContent>
            </Sheet>

            {/* ---------- Journey area drawer / bottom sheet ---------- */}
            <JourneyAreaSheet
                areaKey={areaSheetKey}
                onClose={() => setAreaSheetKey(null)}
                isMobile={isMobile}
                alzheimersMode={alzheimersMode}
                inCareTeamKeys={inCareTeamKeys}
                visitedKeys={visitedKeys}
                onToggleVisited={toggleVisited}
            />

            {/* ---------- Transition pin detail ---------- */}
            <TransitionPinDialog event={pinEvent} onClose={() => setPinEvent(null)} />

            <SomethingChangedModal
                open={changedOpen}
                onClose={() => setChangedOpen(false)}
                sid={sid}
                onSaved={onEventSaved}
                onUpdateWhere={() => { setChangedOpen(false); setWhereOpen(true); }}
            />
            <UpdateWhereModal
                open={whereOpen}
                onClose={() => setWhereOpen(false)}
                primaryKey={primaryKey}
                alsoKeys={alsoKeys}
                onSave={saveWhere}
            />
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Support lane (desktop) + Memory Lane Folder                         */
/* ------------------------------------------------------------------ */

const SUPPORT_ITEMS = [
    { key: "lane", label: "Talk to Lane", to: "/chat", icon: MessageCircle, detail: "Ask anything, anytime." },
    { key: "wellness", label: "Caregiver wellness", to: "/wellness", icon: LifeBuoy, detail: "A quiet pulse on how you're holding up." },
    { key: "coordination", label: "Care coordination", to: "/care-team", icon: HeartHandshake, detail: "Keep the team on the same page." },
    { key: "community", label: "Community support", to: "/care-network", icon: Users, detail: "Groups, helplines, people who get it." },
];

function SupportLane() {
    return (
        <section
            className="rounded-2xl border border-cream-dark bg-white p-3.5 relative"
            data-testid="support-lane-card"
            style={{ borderLeft: "3px dashed #7FB0AE" }}
        >
            <p className="text-[10px] uppercase tracking-wider font-semibold text-teal-dark leading-snug">
                Support available throughout the journey
            </p>
            <p className="mt-1 text-[11px] text-[#6E7A74] leading-snug">
                Pull over anytime. These supports stay available even when providers and programs change.
            </p>
            <div className="mt-2.5 space-y-1.5">
                {SUPPORT_ITEMS.map(({ key, label, to, icon: Icon }) => (
                    <Link
                        key={key}
                        to={to}
                        data-testid={`support-chip-${key}`}
                        className="flex items-center gap-2 rounded-xl border border-cream-dark bg-cream-light/50 px-2.5 py-2 text-xs font-medium text-[#1F2624] hover:bg-teal-soft/60 hover:border-teal/40 transition-colors focus-visible:ring-2 focus-visible:ring-teal outline-none"
                    >
                        <span className="w-6 h-6 rounded-lg bg-white border border-cream-dark flex items-center justify-center shrink-0">
                            <Icon size={12} strokeWidth={2.2} className="text-teal-dark" />
                        </span>
                        {label}
                    </Link>
                ))}
            </div>
        </section>
    );
}

function MemoryLaneFolder() {
    return (
        <Link
            to="/roadmap/story"
            data-testid="memory-lane-folder"
            className="block rounded-2xl border border-cream-dark bg-cream-light p-3.5 hover:bg-teal-soft/40 transition-colors focus-visible:ring-2 focus-visible:ring-teal outline-none"
        >
            <p className="text-[10px] uppercase tracking-wider font-semibold text-[#4C5551] flex items-center gap-1.5">
                <FolderOpen size={13} strokeWidth={2.2} className="text-teal-dark" /> Your Memory Lane Folder
            </p>
            <p className="mt-1.5 text-[11px] text-[#4C5551] leading-snug">
                Your records, notes, medication lists, care plans, provider information, questions, and service
                history stay together as you move through the journey.
            </p>
            <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-teal-dark">
                Open documents and history <ArrowRight size={11} strokeWidth={2.2} />
            </span>
        </Link>
    );
}

/* ------------------------------------------------------------------ */
/* Who is on the road with you (ACTUAL services, never suggestions)    */
/* ------------------------------------------------------------------ */

function WhoIsOnTheRoad({ providers, onUpdate }) {
    const [view, setView] = useState("map");
    const [selectedId, setSelectedId] = useState(null);
    const selected = providers.find(p => p.id === selectedId) || null;

    const groups = useMemo(() => ({
        active: providers.filter(p => normalizeStatus(p.status) === "active"),
        referred: providers.filter(p => normalizeStatus(p.status) === "referred"),
        paused: providers.filter(p => normalizeStatus(p.status) === "paused"),
        ended: providers.filter(p => normalizeStatus(p.status) === "ended"),
    }), [providers]);

    return (
        <section className="ml-card p-4 md:p-5" data-testid="roadmap-your-services" style={{ borderLeft: "5px solid #1D8A85" }}>
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-[10px] uppercase tracking-wider font-semibold text-teal-dark">
                    Who is on the road with you
                </h2>
                <div className="flex items-center gap-2">
                    {providers.length > 0 && (
                        <div className="flex rounded-full border border-cream-dark overflow-hidden text-[11px] font-semibold" data-testid="roadmap-services-view-toggle">
                            {[["map", "Map"], ["list", "List"]].map(([v, label]) => (
                                <button key={v} type="button" onClick={() => setView(v)}
                                    data-testid={`roadmap-services-view-${v}`}
                                    className={`px-4 py-1 min-h-[44px] transition-colors focus-visible:ring-2 focus-visible:ring-teal outline-none ${view === v ? "bg-teal-dark text-white" : "bg-white text-[#4C5551] hover:bg-cream-light"}`}>
                                    {label}
                                </button>
                            ))}
                        </div>
                    )}
                    <Link to="/care-team" className="text-xs text-teal-dark hover:underline inline-flex items-center gap-1 min-h-[44px]" data-testid="roadmap-manage-care-team">
                        Manage <ArrowRight size={11} strokeWidth={2} />
                    </Link>
                </div>
            </div>
            <p className="mt-1 text-xs text-[#6E7A74] leading-snug" data-testid="roadmap-services-explanation">
                This map shows providers and programs you have added to your actual care team. Suggested services
                appear separately inside each journey area.
            </p>

            {providers.length === 0 ? (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3" data-testid="roadmap-services-empty">
                    <p className="text-sm text-[#4C5551]">You have not added any services yet.</p>
                    <Link to="/care-team" className="ml-btn-secondary text-sm" data-testid="roadmap-add-to-care-team">
                        <PlusCircle size={14} strokeWidth={2} /> Add to my care team
                    </Link>
                </div>
            ) : view === "map" ? (
                <div className="mt-3">
                    <CareJourneyFlow providers={providers} selectedId={selectedId} onSelect={setSelectedId} />
                    {selected && (
                        <ProviderDetailCard provider={selected} onUpdate={onUpdate} onClose={() => setSelectedId(null)} />
                    )}
                </div>
            ) : (
                <div className="mt-3 space-y-4" data-testid="roadmap-services-list">
                    {[
                        ["active", "Current services"],
                        ["referred", "Referred or planned"],
                        ["paused", "Paused"],
                        ["ended", "Past services"],
                    ].map(([key, label]) => groups[key].length > 0 && (
                        <div key={key} data-testid={`services-group-${key}`}>
                            <p className="text-[10px] uppercase tracking-wider font-semibold text-[#6E7A74] flex items-center gap-1.5">
                                {key === "ended" && <History size={12} strokeWidth={2} />} {label}
                            </p>
                            <div className="mt-1.5 space-y-2">
                                {groups[key].map(p => (
                                    <ServiceStatusRow key={p.id} provider={p} onUpdate={onUpdate} past={key === "ended"} />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}

/** Detail drawer content for one provider — everything on file, plus edit. */
function ProviderDetailCard({ provider: p, onUpdate, onClose }) {
    const status = normalizeStatus(p.status);
    const typeName = SERVICES[p.service_key]?.name || p.service_name || "Service";
    const pastRuns = (p.history || []).filter(h => h.status === "ended");
    return (
        <div className="mt-3 rounded-2xl border border-teal/30 bg-teal-soft/20 p-3.5" data-testid="care-flow-selected-row">
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#1F2624]">{p.name}</p>
                    <p className="text-xs text-[#4C5551]">{typeName}</p>
                </div>
                <span className={`rounded-2xl px-2.5 py-1 text-xs font-semibold ${STATUS_STYLE[status]}`} data-testid="provider-detail-status">
                    {STATUS_OPTIONS.find(o => o.value === status)?.label || status}
                </span>
            </div>
            <dl className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-[#4C5551]">
                <div data-testid="provider-detail-start"><dt className="inline font-semibold">Started:</dt>{" "}
                    <dd className="inline">{p.start_date ? fmtDate(p.start_date) : "Start date not entered"}</dd></div>
                {(p.end_date || status === "ended") && (
                    <div data-testid="provider-detail-end"><dt className="inline font-semibold">Ended:</dt>{" "}
                        <dd className="inline">{p.end_date ? fmtDate(p.end_date) : (p.ended_at ? fmtDate(p.ended_at) : "—")}</dd></div>
                )}
                {p.phone && (
                    <div data-testid="provider-detail-phone"><dt className="inline font-semibold">Phone:</dt>{" "}
                        <dd className="inline"><a href={`tel:${p.phone}`} className="text-teal-dark underline inline-flex items-center gap-1"><Phone size={10} strokeWidth={2} />{p.phone}</a></dd></div>
                )}
                {p.contact_person && (
                    <div data-testid="provider-detail-contact"><dt className="inline font-semibold">Contact:</dt>{" "}
                        <dd className="inline">{p.contact_person}</dd></div>
                )}
                {p.notes && (
                    <div className="sm:col-span-2" data-testid="provider-detail-notes"><dt className="inline font-semibold">Why they&apos;re involved:</dt>{" "}
                        <dd className="inline">{p.notes}</dd></div>
                )}
                {p.note && (
                    <div className="sm:col-span-2" data-testid="provider-detail-note"><dt className="inline font-semibold">Status note:</dt>{" "}
                        <dd className="inline">{p.note}</dd></div>
                )}
                {pastRuns.length > 0 && (
                    <div className="sm:col-span-2" data-testid="provider-detail-episodes">
                        <dt className="inline font-semibold">Previous episodes:</dt>{" "}
                        <dd className="inline">
                            {pastRuns.map((h, i) => {
                                const bits = [h.start_date ? fmtDate(h.start_date) : null, h.end_date ? fmtDate(h.end_date) : (h.ended_at ? fmtDate(h.ended_at) : null)].filter(Boolean);
                                return <span key={i}>{i > 0 ? "; " : ""}{bits.join(" – ") || "dates not entered"}</span>;
                            })}
                            {status !== "ended" && p.start_date ? ` · Restarted ${fmtDate(p.start_date)}` : ""}
                        </dd>
                    </div>
                )}
            </dl>
            <div className="mt-2.5">
                <ServiceStatusRow provider={p} onUpdate={onUpdate} compact />
            </div>
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
                <Link to="/care-team" className="ml-btn-secondary !px-3 !py-1.5 text-xs" data-testid="provider-detail-manage">
                    Manage service <ArrowRight size={12} strokeWidth={2} />
                </Link>
                <button type="button" onClick={onClose} className="text-xs text-[#6E7A74] hover:text-[#1F2624]" data-testid="provider-detail-close">
                    Close
                </button>
            </div>
        </div>
    );
}

function ServiceStatusRow({ provider: p, onUpdate, past = false, compact = false }) {
    const [editOpen, setEditOpen] = useState(false);
    const [form, setForm] = useState({ start_date: p.start_date || "", end_date: p.end_date || "", note: p.note || "" });
    const status = normalizeStatus(p.status);
    const typeName = SERVICES[p.service_key]?.name || p.service_name || "Service";
    const prevEpisodes = (p.history || []).filter(h => h.status === "ended").length;

    const dateBits = [];
    if (p.start_date) dateBits.push(`${prevEpisodes > 0 && status !== "ended" ? "Restarted" : "Started"} ${fmtDate(p.start_date)}`);
    else if (status !== "ended") dateBits.push("Start date not entered");
    if (p.end_date) dateBits.push(`Ended ${fmtDate(p.end_date)}`);
    else if (status === "ended" && p.ended_at) dateBits.push(`Ended ${fmtDate(p.ended_at)}`);

    return (
        <div className={`rounded-xl border border-cream-dark bg-white p-3 ${past ? "opacity-80" : ""}`} data-testid={`service-row-${p.id}`}>
            <div className="flex flex-wrap items-center gap-2">
                <div className="flex-1 min-w-0">
                    {!compact && (
                        <p className="text-sm font-medium text-[#1F2624]">
                            {p.name}
                            <span className="text-[#6E7A74] font-normal"> · {typeName}</span>
                        </p>
                    )}
                    <p className="text-xs text-[#6E7A74] mt-0.5">
                        {dateBits.join(" · ")}
                        {!compact && p.note ? `${dateBits.length ? " · " : ""}${p.note}` : ""}
                        {prevEpisodes > 0 && status !== "ended" && (
                            <span className="italic" data-testid={`service-restarted-${p.id}`}> · previously ended, restarted</span>
                        )}
                    </p>
                </div>
                <select
                    value={status}
                    onChange={(e) => onUpdate(p, { status: e.target.value })}
                    data-testid={`service-status-${p.id}`}
                    aria-label={`Status for ${p.name}`}
                    className={`rounded-2xl px-2.5 py-1 text-xs font-semibold border-0 cursor-pointer focus-visible:ring-2 focus-visible:ring-teal outline-none ${STATUS_STYLE[status] || STATUS_STYLE.active}`}
                >
                    {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <button
                    type="button"
                    onClick={() => setEditOpen(v => !v)}
                    className="p-1.5 rounded-lg text-[#6E7A74] hover:bg-cream-light focus-visible:ring-2 focus-visible:ring-teal outline-none"
                    data-testid={`service-edit-details-${p.id}`}
                    title="Dates and notes"
                    aria-label={`Edit dates and notes for ${p.name}`}
                >
                    <Pencil size={13} strokeWidth={1.8} />
                </button>
            </div>

            {editOpen && (
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid={`service-details-form-${p.id}`}>
                    <label className="text-[10px] uppercase tracking-wider font-semibold text-[#6E7A74]">
                        Start date
                        <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                            className="mt-1 w-full rounded-lg border border-cream-dark px-2 py-1.5 text-xs font-normal normal-case tracking-normal" />
                    </label>
                    <label className="text-[10px] uppercase tracking-wider font-semibold text-[#6E7A74]">
                        End date
                        <input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                            className="mt-1 w-full rounded-lg border border-cream-dark px-2 py-1.5 text-xs font-normal normal-case tracking-normal" />
                    </label>
                    <label className="text-[10px] uppercase tracking-wider font-semibold text-[#6E7A74]">
                        Reason / note
                        <input type="text" value={form.note} placeholder="e.g., goals met, insurance change"
                            onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                            className="mt-1 w-full rounded-lg border border-cream-dark px-2 py-1.5 text-xs font-normal normal-case tracking-normal" />
                    </label>
                    <div className="sm:col-span-3">
                        <button
                            type="button"
                            className="ml-btn-secondary !px-3 !py-1 text-xs"
                            data-testid={`service-details-save-${p.id}`}
                            onClick={() => { onUpdate(p, form); setEditOpen(false); }}
                        >
                            Save details
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Journey areas — compact rows on a thin road; details in a drawer    */
/* ------------------------------------------------------------------ */

function JourneyAreaRow({ area, isFirst, isLast, isPrimary, isAlso, isVisited, pins, alzheimersMode, onOpen, onOpenPin }) {
    const Icon = ICON_MAP[area.icon] || MapPin;
    return (
        <li
            id={`roadmap-stage-${area.key}`}
            data-testid={`roadmap-stage-${area.key}`}
            className="relative grid grid-cols-[28px_1fr] gap-x-2"
        >
            {/* Thin straight road rail */}
            <div className="relative">
                <span
                    aria-hidden="true"
                    className="absolute left-1/2 -translate-x-1/2 w-[3px] bg-[#EBD68F]"
                    style={{ top: isFirst ? "1.4rem" : 0, bottom: isLast ? "auto" : 0, height: isLast ? "1.4rem" : undefined }}
                />
                {isPrimary ? (
                    <span aria-hidden="true" className="absolute left-1/2 -translate-x-1/2 top-3.5 flex w-6 h-6 items-center justify-center" data-testid="gps-you-are-here">
                        <span className="absolute inline-flex h-full w-full rounded-full opacity-40 animate-ping" style={{ backgroundColor: area.color }} />
                        <span className="relative w-4 h-4 rounded-full border-[3px] border-white shadow-md" style={{ backgroundColor: area.color }} />
                    </span>
                ) : (
                    <span
                        aria-hidden="true"
                        className="absolute left-1/2 -translate-x-1/2 top-4 w-3 h-3 rounded-full border-2 border-white shadow-sm"
                        style={{ backgroundColor: area.color }}
                    />
                )}
            </div>

            {/* Compact row */}
            <div className="pb-2.5 min-w-0">
                <button
                    type="button"
                    onClick={onOpen}
                    aria-haspopup="dialog"
                    data-testid={`roadmap-stage-toggle-${area.key}`}
                    className={`w-full text-left rounded-2xl border px-3.5 py-3 transition-[filter] hover:brightness-[0.97] focus-visible:ring-2 focus-visible:ring-teal outline-none ${isPrimary ? "ring-1" : ""}`}
                    style={{ backgroundColor: area.color_soft, borderColor: `${area.color}33`, ...(isPrimary ? { "--tw-ring-color": `${area.color}66` } : {}) }}
                >
                    <div className="flex items-center gap-2.5">
                        <span className="shrink-0 w-8 h-8 rounded-xl flex items-center justify-center shadow-sm" style={{ backgroundColor: area.color }}>
                            <Icon strokeWidth={1.8} size={15} className="text-white" />
                        </span>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="text-base md:text-lg font-heading font-medium text-[#1F2624] leading-tight">
                                    {area.title}
                                </h3>
                                {isPrimary && (
                                    <span className="text-[9px] uppercase tracking-wider font-bold rounded-2xl px-2 py-0.5 text-white" style={{ backgroundColor: area.color }} data-testid={`you-are-here-pill-${area.key}`}>
                                        You are here
                                    </span>
                                )}
                                {isAlso && (
                                    <span className="text-[9px] uppercase tracking-wider font-semibold rounded-2xl px-2 py-0.5 border" style={{ color: area.color, borderColor: area.color }} data-testid={`also-dealing-pill-${area.key}`}>
                                        Also dealing with
                                    </span>
                                )}
                                {isVisited && (
                                    <span className="text-[9px] uppercase tracking-wider font-semibold rounded-2xl px-2 py-0.5 bg-white/70 text-[#4C5551] border border-cream-dark" data-testid={`been-here-pill-${area.key}`}>
                                        ✓ Been here
                                    </span>
                                )}
                            </div>
                            {alzheimersMode && area.fast_label && (
                                <p data-testid={`stage-fast-label-${area.key}`} className="mt-0.5 text-[10px] text-[#6E7A74] italic">
                                    Clinical: {area.fast_label}
                                </p>
                            )}
                            <p className="mt-0.5 text-xs text-[#4C5551] leading-snug line-clamp-1">{area.tagline}</p>
                            {isPrimary && (
                                <p className="mt-1 text-xs text-[#1F2624] leading-snug line-clamp-2" data-testid={`stage-primary-meaning-${area.key}`}>
                                    {area.meaning}
                                </p>
                            )}
                        </div>
                        <span className="shrink-0 inline-flex items-center gap-0.5 text-[11px] font-semibold" style={{ color: area.color }}>
                            Details <ChevronRight size={13} strokeWidth={2.2} />
                        </span>
                    </div>
                </button>
                {pins.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1.5 pl-1" data-testid={`area-pins-${area.key}`}>
                        {pins.map(ev => (
                            <TransitionPin key={ev.id} event={ev} onOpen={() => onOpenPin(ev)} />
                        ))}
                    </div>
                )}
            </div>
        </li>
    );
}

/** Compact event pin — a recorded change on the road. */
function TransitionPin({ event, onOpen }) {
    return (
        <button
            type="button"
            onClick={onOpen}
            data-testid={`transition-pin-${event.id}`}
            aria-label={`Change recorded: ${event.label} on ${fmtDate(event.date)}`}
            className="inline-flex items-center gap-1 rounded-2xl border border-[#D9C8F8] bg-[#F3EDFC] px-2 py-0.5 text-[10px] font-medium text-[#6D4BC0] hover:bg-[#EBE2FC] transition-colors focus-visible:ring-2 focus-visible:ring-teal outline-none"
        >
            <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rotate-45 bg-[#8B5CF6]" />
            {event.label} · {fmtDate(event.date)}
        </button>
    );
}

function TransitionPinDialog({ event, onClose }) {
    if (!event) return null;
    const area = JOURNEY_AREAS.find(a => a.key === event.journey_area);
    return (
        <Dialog open={!!event} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="max-w-md" data-testid="transition-pin-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <span aria-hidden="true" className="inline-block w-2.5 h-2.5 rotate-45 bg-[#8B5CF6]" />
                        {event.label}
                    </DialogTitle>
                    <DialogDescription>{fmtDate(event.date)}{area ? ` · during ${area.title}` : ""}</DialogDescription>
                </DialogHeader>
                <div className="space-y-2 text-sm text-[#1F2624]">
                    {event.notes && <p data-testid="pin-notes"><span className="font-semibold">What changed:</span> {event.notes}</p>}
                    {!event.notes && <p className="text-[#6E7A74] italic">No notes were added for this change.</p>}
                    {event.related_services?.length > 0 && (
                        <p data-testid="pin-services"><span className="font-semibold">Services affected:</span> {event.related_services.join(", ")}</p>
                    )}
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                    <Link
                        to={`/chat?prompt=${encodeURIComponent(`On ${fmtDate(event.date)} we recorded a change: ${event.label}${event.notes ? ` — ${event.notes}` : ""}. Help me think through follow-up actions.`)}`}
                        className="ml-btn-primary text-sm" data-testid="pin-talk-to-lane"
                    >
                        <MessageCircle size={14} strokeWidth={2} /> Follow up with Lane
                    </Link>
                    <Link to="/care-team" className="ml-btn-secondary text-sm" data-testid="pin-manage-services">
                        Update care team
                    </Link>
                </div>
            </DialogContent>
        </Dialog>
    );
}

/* ------------------------------------------------------------------ */
/* Journey area detail drawer (desktop) / bottom sheet (mobile)        */
/* ------------------------------------------------------------------ */

function JourneyAreaSheet({ areaKey, onClose, isMobile, alzheimersMode, inCareTeamKeys, visitedKeys = [], onToggleVisited }) {
    const area = JOURNEY_AREAS.find(a => a.key === areaKey);
    const visited = area ? visitedKeys.includes(area.key) : false;
    return (
        <Sheet open={!!area} onOpenChange={(v) => { if (!v) onClose(); }}>
            <SheetContent
                side={isMobile ? "bottom" : "right"}
                className={isMobile ? "rounded-t-3xl max-h-[88vh] overflow-y-auto" : "w-full sm:max-w-xl overflow-y-auto"}
                data-testid="journey-area-sheet"
            >
                {area && (
                    <>
                        <SheetHeader className="text-left">
                            <SheetTitle className="flex items-center gap-2.5">
                                <span className="shrink-0 w-8 h-8 rounded-xl flex items-center justify-center" style={{ backgroundColor: area.color }}>
                                    {React.createElement(ICON_MAP[area.icon] || MapPin, { size: 15, strokeWidth: 1.8, className: "text-white" })}
                                </span>
                                {area.title}
                            </SheetTitle>
                            <SheetDescription>
                                {area.tagline}
                                {alzheimersMode && area.fast_label ? ` — Clinical context: ${area.fast_label}` : ""}
                            </SheetDescription>
                            {onToggleVisited && (
                                <button
                                    type="button"
                                    onClick={() => onToggleVisited(area.key)}
                                    data-testid={`sheet-been-here-${area.key}`}
                                    className={`mt-2 self-start inline-flex items-center gap-1.5 rounded-2xl border px-3 py-1.5 text-xs font-semibold transition-colors ${visited ? "text-white" : "bg-white text-[#4C5551] border-cream-dark hover:border-teal hover:text-teal-dark"}`}
                                    style={visited ? { backgroundColor: area.color, borderColor: area.color } : {}}
                                >
                                    <CheckCircle2 size={13} strokeWidth={2} />
                                    {visited ? "Been here — tap to unmark" : "Mark as \u201cbeen here\u201d"}
                                </button>
                            )}
                        </SheetHeader>

                        <div className="mt-4 space-y-5 pb-8" data-testid={`journey-sheet-body-${area.key}`}>
                            <StageBlock testid={`stage-meaning-${area.key}`} icon={Info} label="What this part of the journey may look like" color={area.color}>
                                <p className="text-sm text-[#1F2624] leading-relaxed">{area.meaning}</p>
                            </StageBlock>
                            <StageBlock testid={`stage-concerns-${area.key}`} icon={MessageCircle} label="Common caregiver concerns" color={area.color}>
                                <BulletList items={area.concerns} tone="quote" />
                            </StageBlock>
                            <StageBlock testid={`stage-watch-${area.key}`} icon={Eye} label="What to watch for" color={area.color}>
                                <BulletList items={area.watch_for} />
                            </StageBlock>
                            <StageBlock testid={`stage-next-${area.key}`} icon={ArrowRight} label="Next best steps" color={area.color} accent>
                                <BulletList items={area.next_steps} />
                            </StageBlock>
                            <StageBlock testid={`stage-doctor-${area.key}`} icon={Stethoscope} label="Questions to ask the doctor or care team" color={area.color}>
                                <BulletList items={area.doctor_questions} tone="doctor" />
                            </StageBlock>
                            <StageBlock testid={`stage-docs-${area.key}`} icon={FileText} label="Documents to prepare" color={area.color}>
                                <BulletList items={area.documents} />
                            </StageBlock>
                            <StageBlock testid={`stage-services-${area.key}`} icon={Users} label="Services that may help during this part of the journey" color={area.color}>
                                <ServiceChips services={area.services} inCareTeamKeys={inCareTeamKeys} />
                            </StageBlock>
                            <StageBlock testid={`stage-lane-${area.key}`} icon={Sparkles} label="How Lane can help" color={area.color}>
                                <BulletList items={area.lane_help} tone="lane" />
                                <Link
                                    to={`/chat?prompt=${encodeURIComponent(`I'm dealing with "${area.title}" on my Memory Lane roadmap. Can you walk me through what to focus on right now?`)}`}
                                    className="ml-btn-primary mt-3 inline-flex text-sm"
                                    data-testid={`stage-talk-to-lane-${area.key}`}
                                >
                                    <MessageCircle size={14} strokeWidth={2} /> Talk to Lane about this area
                                </Link>
                            </StageBlock>
                        </div>
                    </>
                )}
            </SheetContent>
        </Sheet>
    );
}

function StageBlock({ icon: Icon, label, testid, children, color, accent = false }) {
    return (
        <div data-testid={testid} className={accent ? "rounded-2xl bg-cream-light/70 border border-cream-dark p-3.5" : ""}>
            <p className="text-xs uppercase tracking-wider font-semibold mb-2.5 flex items-center gap-2" style={{ color: color || "#3A6C6A" }}>
                {Icon && <Icon size={14} strokeWidth={2} />}
                {label}
            </p>
            {children}
        </div>
    );
}

function BulletList({ items, tone }) {
    if (!items || !items.length) return null;
    return (
        <ul className="space-y-2">
            {items.map((item, i) => (
                <li key={i} className="flex gap-2.5 text-[#1F2624] leading-relaxed text-sm">
                    <span
                        className={`mt-2 w-1.5 h-1.5 rounded-full shrink-0 ${tone === "quote" ? "bg-coral" :
                            tone === "doctor" ? "bg-stage_middle-dark" :
                                tone === "lane" ? "bg-teal" : "bg-teal-light"
                            }`}
                    />
                    <span className={tone === "quote" ? "italic" : ""}>{item}</span>
                </li>
            ))}
        </ul>
    );
}

/**
 * Suggested services as compact chips. These are EXAMPLES — never shown
 * as active, never on the "Who is on the road with you" map unless the
 * caregiver adds them to My Care Team.
 */
function ServiceChips({ services, inCareTeamKeys }) {
    if (!services || !services.length) return null;
    return (
        <div className="flex flex-wrap gap-1.5" data-testid="services-strip">
            {services.map((svc) => {
                const mine = inCareTeamKeys?.has(svc.key);
                return (
                    <Link
                        key={svc.key}
                        to={mine ? "/care-team" : `/care-team?add=${svc.key}`}
                        data-testid={`service-plugin-${svc.key}`}
                        title={svc.note || svc.name}
                        className={`inline-flex items-center gap-1.5 rounded-2xl border px-2.5 py-1 text-xs transition-colors focus-visible:ring-2 focus-visible:ring-teal outline-none ${mine
                            ? "border-teal bg-teal-soft/60 text-teal-dark font-medium"
                            : "border-cream-dark bg-white text-[#4C5551] hover:border-teal-light"
                            }`}
                    >
                        {mine ? (
                            <><CheckCircle2 size={11} strokeWidth={2.2} data-testid={`service-on-your-team-${svc.key}`} /> {svc.name}<span className="font-normal text-[10px]"> · in your care team</span></>
                        ) : (
                            <><PlusCircle size={11} strokeWidth={2} data-testid={`service-add-yours-${svc.key}`} /> {svc.name}</>
                        )}
                    </Link>
                );
            })}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Modals                                                              */
/* ------------------------------------------------------------------ */

/**
 * Something changed — records a real, dated transition event (a pin on
 * the road), then offers follow-ups. Nothing is claimed to be "updated
 * automatically": the caregiver chooses what to update next.
 */
function SomethingChangedModal({ open, onClose, sid, onSaved, onUpdateWhere }) {
    const navigate = useNavigate();
    const [picked, setPicked] = useState(null);
    const [form, setForm] = useState({ date: "", journey_area: "", notes: "" });
    const [savedEvent, setSavedEvent] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setPicked(null);
            setSavedEvent(null);
            setForm({ date: new Date().toISOString().slice(0, 10), journey_area: "", notes: "" });
        }
    }, [open]);

    const save = async () => {
        setSaving(true);
        try {
            const r = await api.post(`/roadmap/transitions/${sid}`, {
                event_type: picked.key,
                date: form.date,
                journey_area: form.journey_area || null,
                notes: form.notes || null,
            });
            setSavedEvent(r.data.event);
            onSaved(r.data.event);
            toast.success("Change recorded on your roadmap.");
        } catch {
            toast.error("Couldn't save the change. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="max-w-md" data-testid="something-changed-modal">
                {!picked ? (
                    <>
                        <DialogHeader>
                            <DialogTitle>What changed?</DialogTitle>
                            <DialogDescription>
                                Pick the closest one. You can save it as a dated event on your roadmap and talk it
                                through with Lane.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="grid grid-cols-1 gap-1.5 max-h-[50vh] overflow-y-auto pr-1">
                            {CHANGE_OPTIONS.map((opt) => (
                                <button
                                    key={opt.key}
                                    type="button"
                                    data-testid={`sc-option-${opt.key}`}
                                    onClick={() => setPicked(opt)}
                                    className="text-left rounded-xl border border-cream-dark bg-white px-3.5 py-2.5 text-sm text-[#1F2624] hover:bg-cream-light transition-colors focus-visible:ring-2 focus-visible:ring-teal outline-none"
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </>
                ) : !savedEvent ? (
                    <>
                        <DialogHeader>
                            <DialogTitle>{picked.label}</DialogTitle>
                            <DialogDescription>
                                When did this happen? A short note helps future-you (and Lane) remember what changed.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-3" data-testid="sc-event-form">
                            <label className="block text-[10px] uppercase tracking-wider font-semibold text-[#6E7A74]">
                                Date
                                <input type="date" value={form.date}
                                    onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                                    data-testid="sc-event-date"
                                    className="mt-1 w-full rounded-lg border border-cream-dark px-2.5 py-2 text-sm font-normal normal-case tracking-normal" />
                            </label>
                            <label className="block text-[10px] uppercase tracking-wider font-semibold text-[#6E7A74]">
                                Part of the journey (optional)
                                <select value={form.journey_area}
                                    onChange={e => setForm(f => ({ ...f, journey_area: e.target.value }))}
                                    data-testid="sc-event-area"
                                    className="mt-1 w-full rounded-lg border border-cream-dark px-2.5 py-2 text-sm font-normal normal-case tracking-normal bg-white">
                                    <option value="">Not tied to one area</option>
                                    {JOURNEY_AREAS.map(a => <option key={a.key} value={a.key}>{a.title}</option>)}
                                </select>
                            </label>
                            <label className="block text-[10px] uppercase tracking-wider font-semibold text-[#6E7A74]">
                                What changed (optional)
                                <textarea rows={2} value={form.notes} placeholder="A sentence is plenty."
                                    onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                                    data-testid="sc-event-notes"
                                    className="mt-1 w-full rounded-lg border border-cream-dark px-2.5 py-2 text-sm font-normal normal-case tracking-normal" />
                            </label>
                        </div>
                        <div className="flex flex-wrap gap-2 pt-1">
                            <button type="button" onClick={save} disabled={saving} className="ml-btn-primary text-sm disabled:opacity-50" data-testid="sc-event-save">
                                {saving ? "Saving…" : "Save change"}
                            </button>
                            <button
                                type="button"
                                onClick={() => { onClose(); navigate(`/chat?prompt=${encodeURIComponent(picked.prompt)}`); }}
                                className="ml-btn-secondary text-sm" data-testid="sc-skip-to-lane"
                            >
                                Skip — just talk to Lane
                            </button>
                            <button type="button" onClick={() => setPicked(null)} className="text-xs text-[#6E7A74] hover:text-[#1F2624] self-center" data-testid="sc-back">
                                Back
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <DialogHeader>
                            <DialogTitle>Change recorded.</DialogTitle>
                            <DialogDescription>
                                {picked.label} · {fmtDate(savedEvent.date)} is now a pin on your roadmap. What would help next?
                            </DialogDescription>
                        </DialogHeader>
                        <div className="grid grid-cols-1 gap-1.5" data-testid="sc-followups">
                            <button
                                type="button"
                                onClick={() => { onClose(); navigate(`/chat?prompt=${encodeURIComponent(picked.prompt)}`); }}
                                className="text-left rounded-xl border border-cream-dark bg-white px-3.5 py-2.5 text-sm hover:bg-cream-light transition-colors" data-testid="sc-followup-lane"
                            >
                                <span className="font-medium text-[#1F2624]">Talk it through with Lane</span>
                                <span className="block text-xs text-[#6E7A74]">She&apos;ll help you name the next step.</span>
                            </button>
                            {SERVICE_CHANGE_KEYS.has(picked.key) && (
                                <Link to="/care-team" onClick={onClose}
                                    className="rounded-xl border border-cream-dark bg-white px-3.5 py-2.5 text-sm hover:bg-cream-light transition-colors" data-testid="sc-followup-careteam">
                                    <span className="font-medium text-[#1F2624]">Update my care team</span>
                                    <span className="block text-xs text-[#6E7A74]">Mark a service started, paused, or ended.</span>
                                </Link>
                            )}
                            <button type="button" onClick={onUpdateWhere}
                                className="text-left rounded-xl border border-cream-dark bg-white px-3.5 py-2.5 text-sm hover:bg-cream-light transition-colors" data-testid="sc-followup-where">
                                <span className="font-medium text-[#1F2624]">Update where we are</span>
                                <span className="block text-xs text-[#6E7A74]">Move your primary or &ldquo;also dealing with&rdquo; areas.</span>
                            </button>
                            <button type="button" onClick={onClose} className="text-xs text-[#6E7A74] hover:text-[#1F2624] py-1" data-testid="sc-done">
                                Done for now
                            </button>
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}

function UpdateWhereModal({ open, onClose, primaryKey, alsoKeys, onSave }) {
    const [primary, setPrimary] = useState(primaryKey || "");
    const [also, setAlso] = useState(alsoKeys || []);

    useEffect(() => {
        if (open) { setPrimary(primaryKey || ""); setAlso(alsoKeys || []); }
    }, [open, primaryKey, alsoKeys]);

    const toggleAlso = (key) => {
        setAlso(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="max-w-md" data-testid="update-area-modal">
                <DialogHeader>
                    <DialogTitle>Where are you on the journey?</DialogTitle>
                    <DialogDescription>
                        Pick your main area, and anything else you&apos;re dealing with at the same time.
                        There&apos;s no wrong answer — this can change any time.
                    </DialogDescription>
                </DialogHeader>
                <div className="max-h-[55vh] overflow-y-auto pr-1 space-y-1.5">
                    {JOURNEY_AREAS.map((a) => {
                        const isPrimary = primary === a.key;
                        const isAlso = also.includes(a.key) && !isPrimary;
                        return (
                            <div key={a.key} className="flex items-center gap-2 rounded-xl border border-cream-dark bg-white px-3 py-2">
                                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: a.color }} />
                                <span className="flex-1 text-sm text-[#1F2624]">{a.title}</span>
                                <button
                                    type="button"
                                    data-testid={`area-primary-${a.key}`}
                                    onClick={() => { setPrimary(a.key); setAlso(prev => prev.filter(k => k !== a.key)); }}
                                    className={`text-[10px] uppercase tracking-wider font-semibold rounded-2xl px-2 py-1 border transition-colors ${isPrimary ? "text-white border-transparent" : "text-[#4C5551] border-cream-dark hover:border-teal"}`}
                                    style={isPrimary ? { backgroundColor: a.color } : {}}
                                >
                                    {isPrimary ? "Primary" : "Set primary"}
                                </button>
                                <button
                                    type="button"
                                    data-testid={`area-also-${a.key}`}
                                    onClick={() => { if (!isPrimary) toggleAlso(a.key); }}
                                    disabled={isPrimary}
                                    className={`text-[10px] uppercase tracking-wider font-semibold rounded-2xl px-2 py-1 border transition-colors disabled:opacity-30 ${isAlso ? "border-transparent" : "text-[#4C5551] border-cream-dark hover:border-teal"}`}
                                    style={isAlso ? { backgroundColor: a.color_soft, color: a.color } : {}}
                                >
                                    {isAlso ? "Also ✓" : "Also"}
                                </button>
                            </div>
                        );
                    })}
                </div>
                <div className="flex gap-2 pt-1">
                    <button type="button" className="ml-btn-primary text-sm" data-testid="update-area-save" onClick={() => onSave(primary, also.filter(k => k !== primary))}>
                        Save
                    </button>
                    <button type="button" className="ml-btn-secondary text-sm" onClick={onClose} data-testid="update-area-cancel">
                        Cancel
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
